import {
  WSActions,
  WSCard,
  WSGrid,
  WSList
} from "@wingspanhq/fe-component-library";
import {
  IRequirementDefinitionResponse,
  RequirementType
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { DataItem } from "../../../../../shared/components/DataItem";
import { cleanOptionalFormValues } from "../../../../../shared/utils/cleanOptionalFormValues";
import { RequirementInstructionsView } from "../../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { CreateRequirementDefinitionFormValues } from "../../../../Requirements/types";
import { useCreateBackgroundChecksRequirementDefinition } from "../../../modules/BackgroundChecks/createRequirementDefinition/useCreateBackgroundChecksRequirementDefinition";
import { useCreateIdentityVerificationRequirementDefinition } from "../../../modules/IdentityVerification/createRequirementDefinition/useCreateIdentityVerificationRequirementDefinition";
import { useCreateSharedFileRequirementDefinition } from "../../../modules/SharedFile/createRequirementDefinition/useCreateSharedFileRequirementDefinition";
import { useCreateSignatureRequirementDefinition } from "../../../modules/Signature/createRequirementDefinition/useCreateSignatureRequirementDefinition";
import { RequirementDefinitionTemplate } from "../types";
import { useCreateInsuranceCoverageRequirementDefinition } from "../../../modules/InsuranceCoverage/createRequirementDefinition/useCreateInsuranceCoverageRequirementDefinition";
import { getRequirementDefinitionTemplateByParams } from "../../../utils/getRequirementDefinitionTemplateByParams";
import { InsuranceInstructionsView } from "../components/InsuranceInstructionsView";
import { AcceptedInsuranceCoverage } from "../../../../../services/api/insurance/monitor/types";

type Props = {
  onBack: () => void;
  onSuccess: (response: IRequirementDefinitionResponse) => void;
  formData: CreateRequirementDefinitionFormValues;
  template: RequirementDefinitionTemplate;
  engagementId?: string;
};

export const StepReviewAndCreate: React.FC<Props> = ({
  template,
  engagementId,
  onSuccess,
  onBack,
  formData
}) => {
  const { name, type, acceptWingspanMasterPolicy } =
    formData as typeof formData & { acceptWingspanMasterPolicy?: boolean };

  const signatureRequirementCreation = useCreateSignatureRequirementDefinition({
    engagementId,
    onSuccess
  });

  const sharedFileRequirementCreation =
    useCreateSharedFileRequirementDefinition({
      engagementId,
      onSuccess
    });

  const backgroundCheckRequirementCreation =
    useCreateBackgroundChecksRequirementDefinition({
      engagementId,
      onSuccess
    });

  const identityVerificationRequirementCreation =
    useCreateIdentityVerificationRequirementDefinition({
      engagementId,
      onSuccess
    });

  const insuranceCoverageRequirementCreation =
    useCreateInsuranceCoverageRequirementDefinition({
      engagementId,
      onSuccess
    });

  return (
    <WSList gap="2XL">
      <WSCard header="Requirement preview">
        <WSGrid gutter={{ m: "2XL" }}>
          <WSGrid.Item span={{ m: "6" }}>
            <DataItem value={name} label="Name" />
          </WSGrid.Item>

          <WSGrid.Item span={{ m: "6" }}>
            <DataItem
              icon={template.icon}
              value={template.name}
              secondaryText={template.helperText}
              label="Type"
            />
          </WSGrid.Item>
        </WSGrid>
      </WSCard>

      {template.instructions || formData.instructions || template.isPolicy ? (
        <WSCard
          header={{
            label: {
              text: "Instructions to contractors",
              helperText:
                "The following instructions will be shown to the contractors."
            }
          }}
        >
          <WSList gap="2XL">
            {template.isPolicy ? (
              <InsuranceInstructionsView
                allowMasterPolicy={acceptWingspanMasterPolicy}
                insuranceName={template.name}
              />
            ) : null}
            {!template.isPolicy &&
            (template.instructions || formData.instructions) ? (
              <WSCard>
                <RequirementInstructionsView
                  instructions={template.instructions || formData.instructions}
                />
              </WSCard>
            ) : null}
          </WSList>
        </WSCard>
      ) : null}

      <WSErrorMessage contextKey="Other" />

      <WSActions
        alignment="fill"
        buttons={[
          {
            kind: "Tertiary",
            label: "Back",
            onClick: onBack,
            type: "button"
          },
          {
            label: "Create requirement",
            type: "submit",
            onAsyncClick: async () => {
              const submitByType: Record<
                RequirementType,
                (
                  formValues: CreateRequirementDefinitionFormValues
                ) => Promise<void>
              > = {
                [RequirementType.Signature]:
                  signatureRequirementCreation.onSubmit,
                [RequirementType.SharedFile]:
                  sharedFileRequirementCreation.onSubmit,
                [RequirementType.BackgroundCheck]:
                  backgroundCheckRequirementCreation.onSubmit,
                [RequirementType.IdentityVerification]:
                  identityVerificationRequirementCreation.onSubmit,
                [RequirementType.InsuranceCoverage]:
                  insuranceCoverageRequirementCreation.onSubmit,
                [RequirementType.Registration]: async () => {},
                [RequirementType.TaxVerification]: async () => {},
                [RequirementType.PayoutMethod]: async () => {}
              };

              const mappedFormValues = template.form?.mapFormData
                ? template.form?.mapFormData(formData, template)
                : formData;

              const cleanFormValues = cleanOptionalFormValues(
                mappedFormValues,
                {
                  ignore: ["coverages"]
                }
              );

              const isSupportedByTemplates =
                getRequirementDefinitionTemplateByParams({
                  type,
                  contextType: cleanFormValues.contextType,
                  policyType: cleanFormValues.coverages?.map(
                    (c: AcceptedInsuranceCoverage) => c.policyType
                  ),
                  strict: true
                });

              if (submitByType[type] && isSupportedByTemplates) {
                await submitByType[type](cleanFormValues);
              } else {
                throw new Error("Invalid requirement type");
              }
            }
          }
        ]}
      />
    </WSList>
  );
};
